import React, { useEffect, useState, useCallback, memo } from "react";
import PrimeButton from "../../../components/PrimeButton";

// Memoize the slide data to prevent unnecessary recalculations
const slides = [
  {
    src: require("../../../assets/Hero/Image1.png"),
    alt: "Slide 1",
    // smalltxt: "Bringing Hope Based on",
    bigtxt: <><span>{"Empowering Healthcare"}</span><br/><span>{"Excellence in Egypt"}</span></>,
    description:
      "H2A Medical Solutions aims to provide advanced, comprehensive, and sustainable healthcare solutions to all healthcare providers, both public and private, in Egypt. With a strong focus on quality, excellence, and trust, H2A empowers healthcare providers to offer distinctive services and improve patient diagnostic outcomes and experiences. The company is committed to customer satisfaction and success, offering top-notch education, technical support, and post-sales services to ensure customer loyalty and satisfaction.",
  },
  {
    src: require("../../../assets/Hero/Image2.png"),
    alt: "Slide 2",
    // smalltxt: "Medical Ultrasound Equipment",
    bigtxt: <><span>{"Bridging Global Technology"}</span><br/><span>{"with Local Expertise"}</span></>,
    description:
      "Founded in 2018, H2A is a pioneer in providing advanced healthcare solutions in Egypt. As the official distributor of Philips ultrasound systems, H2A offers sales, maintenance, training, and after-sales support. A subsidiary of Samcrete Holding, H2A is inspired by its parent company’s legacy of innovation and growth, striving to become a leading force in the medical industry while upholding high standards of quality and productivity.",
  },
  {
    src: require("../../../assets/Hero/Image3.png"),
    alt: "Slide 3",
    // smalltxt: "Defining New, Clear Beginnings",
    bigtxt: <><span>{"Partnering for Better Care:"}</span><br/><span>{"H2A and Philips"}</span></>,
    description:
      "You can learn about your patient's internal anatomy with the help of H2A, whether they are a new parent or simply in need of a checkup. Fast-processing medical equipment, backed by a 24/7 technical team, is an absolute necessity for all healthcare facilities. Say goodbye to cumbersome devices. H2A aims to deliver a distinctive experience for its clients as well as their patients.",
  },
];

// Memoized Slide component to prevent unnecessary rerenders
const Slide = memo(({ slide, isActive }) => (
  <div
    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
      isActive ? "opacity-100" : "opacity-0"
    }`}
    data-carousel-item
  >
    <img 
      className="w-full h-full object-cover" 
      src={slide.src} 
      alt={slide.alt} 
      loading={isActive ? "eager" : "lazy"} // Lazy load inactive slides
    />
    <div className="absolute top-20 inset-0 flex items-center justify-center text-center p-4">
      <div className="text-white">
        <h2 className="hookrideFont text-2xl leading-10 md:leading-[64.8px] md:text-[72px] mb-5">
          {slide.smalltxt}
          <br />
          <span className="text-4xl leading-5 md:text-[96px] md:leading-[86.4px]">
            {slide.bigtxt}
          </span>
        </h2>
        <p className="leading-[24px] text-sm md:text-[16px] md:mx-[20vw] mx-2 mb-5 max-w-[676px]">
          {slide.description}
        </p>
        <div className="mt-5">
          <PrimeButton />
        </div>
      </div>
    </div>
  </div>
));

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Memoize navigation functions
  const goToPrevious = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prevSlide) => (prevSlide === 0 ? slides.length - 1 : prevSlide - 1));
    // Reset autoplay after user interaction
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const goToNext = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prevSlide) => (prevSlide === slides.length - 1 ? 0 : prevSlide + 1));
    // Reset autoplay after user interaction
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const goToSlide = useCallback((index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
    // Reset autoplay after user interaction
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prevSlide) => (prevSlide === slides.length - 1 ? 0 : prevSlide + 1));
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Pause autoplay when window loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsAutoPlaying(document.visibilityState === 'visible');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div className="relative w-[99vw] h-screen overflow-x-hidden">
      <div id="default-carousel" className="relative w-full h-screen" data-carousel="slide">
        {/* Carousel wrapper */}
        <div className="relative h-full overflow-hidden rounded-lg">
          {slides.map((slide, index) => (
            <Slide 
              key={index} 
              slide={slide} 
              isActive={index === currentSlide} 
            />
          ))}
        </div>

        {/* Slider indicators */}
        <div className="absolute z-40 flex -translate-x-1/2 bottom-5 left-1/2 space-x-3 rtl:space-x-reverse">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                currentSlide === index ? "bg-white" : "bg-gray-400 hover:bg-gray-300"
              }`}
              aria-current={currentSlide === index}
              aria-label={`Slide ${index + 1}`}
              onClick={() => goToSlide(index)}
              data-carousel-slide-to={index}
            />
          ))}
        </div>

        {/* Slider controls */}
        <button
          type="button"
          className="absolute top-0 left-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
          onClick={goToPrevious}
          data-carousel-prev
          aria-label="Previous slide"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70 group-focus:outline-none transition-colors duration-300">
            <svg
              className="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 1 1 5l4 4" />
            </svg>
          </span>
        </button>
        <button
          type="button"
          className="absolute top-0 right-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
          onClick={goToNext}
          data-carousel-next
          aria-label="Next slide"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70 group-focus:outline-none transition-colors duration-300">
            <svg
              className="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default memo(HeroCarousel);